import React, { useCallback, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormGroup from "@mui/material/FormGroup";
import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";

import BaseFilter from "../BaseFilter";

interface CheckboxFilterProps {
  id: string;
  label: string;
  options: string[];
  activeFilter: string[];
  emptyText?: string;
  onChange: (newActiveFilter: string[]) => void;
}

const CheckboxFilter: React.FC<CheckboxFilterProps> = ({
  id,
  label,
  options,
  activeFilter,
  emptyText,
  onChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const openPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closePopover = () => {
    setAnchorEl(null);
  };

  const activeFilterSet = useMemo(() => new Set(activeFilter), [activeFilter]);

  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value, checked } = event.target;
      const newactiveFilterSet = new Set(activeFilterSet);
      if (checked) {
        newactiveFilterSet.add(value);
      } else {
        newactiveFilterSet.delete(value);
      }
      onChange(Array.from(newactiveFilterSet));
    },
    [activeFilterSet, onChange]
  );

  const renderedOptions = useMemo(
    () =>
      options.map((option) => (
        <FormControlLabel
          key={option}
          control={
            <Checkbox
              checked={activeFilterSet.has(option)}
              onChange={handleCheckboxChange}
              value={option}
            />
          }
          label={option}
        />
      )),
    [options, activeFilterSet, handleCheckboxChange]
  );

  return (
    <BaseFilter
      id={id}
      label={label}
      anchorEl={anchorEl}
      openPopover={openPopover}
      onClose={closePopover}
    >
      <Box sx={{ p: 2, maxHeight: 300, overflowY: "auto" }}>
        {options.length > 0 ? (
          <FormGroup>{renderedOptions}</FormGroup>
        ) : (
          <Typography variant="subtitle2" color="grey700" noWrap>
            {emptyText}
          </Typography>
        )}
      </Box>
    </BaseFilter>
  );
};

export default React.memo(CheckboxFilter);
